/**
 * Transfer function (SDR, PQ or HLG) of video files. The compositor needs it to decode pixels to
 * linear light: Chrome hands HDR frames to WebGL as the raw, still-encoded BT.2020 signal (no tone
 * mapping), whereas SDR frames arrive as sRGB-like values.
 */
import { ALL_FORMATS, Input } from 'mediabunny';
import type { MediaInput } from '../types.ts';
import { inputKey, sourceFor } from './input.ts';

export type TransferFunction = 'sdr' | 'pq' | 'hlg';

export function transferOf(colorSpace: VideoColorSpaceInit | null | undefined): TransferFunction {
  // TypeScript's DOM lib doesn't list the HDR transfer characteristics yet.
  switch (colorSpace?.transfer as string | null | undefined) {
    case 'pq':
      return 'pq';
    case 'hlg':
      return 'hlg';
    default:
      return 'sdr';
  }
}

/**
 * Per-input transfer detection for the preview, where video comes from `<video>` elements that
 * don't expose colour metadata. Files that can't be probed count as SDR.
 */
export class TransferCache {
  private known = new Map<string, TransferFunction>();
  private pending = new Set<string>();

  /** `onDetect` is called when a transfer function becomes known. */
  constructor(private readonly onDetect?: () => void) {}

  /** The input's transfer function if known; otherwise starts detection and returns undefined. */
  peek(input: MediaInput): TransferFunction | undefined {
    const key = inputKey(input);
    const hit = this.known.get(key);
    if (hit !== undefined || this.pending.has(key)) return hit;
    this.pending.add(key);
    void detectTransfer(input).then((transfer) => {
      this.pending.delete(key);
      this.known.set(key, transfer);
      this.onDetect?.();
    });
    return undefined;
  }

  clear(): void {
    this.known.clear();
  }
}

export async function detectTransfer(media: MediaInput): Promise<TransferFunction> {
  const input = new Input({ source: sourceFor(media), formats: ALL_FORMATS });
  try {
    const track = await input.getPrimaryVideoTrack();
    return track ? transferOf(await track.getColorSpace()) : 'sdr';
  } catch {
    return 'sdr';
  } finally {
    input.dispose();
  }
}
